import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tag,
  message,
  Typography,
  Modal,
  Form,
  Select,
  Switch,
  Tooltip,
  Popconfirm,
  Drawer,
  List,
  Badge,
  Divider
} from 'antd';
import {
  SearchOutlined,
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  SettingOutlined,
  PlusOutlined,
  UserOutlined,
  CrownOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { userManagementAPI, User, UserFilters, CompanyMembership, Company } from '../../services/userManagementAPI';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({});
  
  // Modal states
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [membershipDrawerVisible, setMembershipDrawerVisible] = useState(false);
  const [addMembershipModalVisible, setAddMembershipModalVisible] = useState(false);
  
  // Selected data
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userMemberships, setUserMemberships] = useState<CompanyMembership[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  
  // Forms
  const [userForm] = Form.useForm();
  const [membershipForm] = Form.useForm();

  useEffect(() => {
    loadUsers();
    loadAvailableCompanies();
  }, [filters]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await userManagementAPI.getUsers(filters);
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        message.error(response.message || 'İstifadəçilər yüklənmədi');
      }
    } catch (error) {
      message.error('İstifadəçilər yüklənmədi');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCompanies = async () => {
    try {
      const response = await userManagementAPI.getAvailableCompanies();
      if (response.success && response.data) {
        setAvailableCompanies(response.data);
      } else {
        console.error('Failed to load companies:', response.message);
        message.error('Şirkətlər yüklənmədi');
      }
    } catch (error) {
      console.error('Şirkətlər yüklənmədi:', error);
      message.error('Şirkətlər yüklənmədi');
    }
  };

  const loadUserMemberships = async (userId: number) => {
    try {
      const response = await userManagementAPI.getUserMemberships(userId);
      if (response.success && response.data) {
        setUserMemberships(response.data);
      }
    } catch (error) {
      message.error('Üzvlüklər yüklənmədi');
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    userForm.resetFields();
    setUserModalVisible(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    userForm.setFieldsValue({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      is_active: user.is_active,
      is_staff: user.is_staff,
      is_superuser: user.is_superuser,
      is_crm_user: user.is_crm_user
    });
    setUserModalVisible(true);
  };

  const handleUserSubmit = async (values: any) => {
    try {
      let response: { success: boolean; message?: string; data?: User; errors?: any };
      
      if (selectedUser) {
        response = await userManagementAPI.updateUser(selectedUser.id, values);
      } else {
        response = await userManagementAPI.createUser(values);
      }

      if (response.success) {
        message.success(response.message);
        setUserModalVisible(false);
        loadUsers();
      } else {
        if (response.errors) {
          Object.keys(response.errors).forEach(key => {
            const errorMessage = Array.isArray(response.errors![key])
              ? response.errors![key][0]
              : response.errors![key];
            message.error(`${key}: ${errorMessage}`);
          });
        } else {
          message.error(response.message);
        }
      }
    } catch (error) {
      message.error('Əməliyyat tamamlanmadı');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      const response = await userManagementAPI.deleteUser(userId);
      if (response.success) {
        message.success(response.message);
        loadUsers();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error('İstifadəçi silinmədi');
    }
  };

  const handleManageMemberships = async (user: User) => {
    setSelectedUser(user);
    await loadUserMemberships(user.id);
    setMembershipDrawerVisible(true);
  };

  const handleAddMembership = async () => {
    membershipForm.resetFields();
    await loadAvailableCompanies(); // Şirkətləri yenidən yüklə
    setAddMembershipModalVisible(true);
  };

  const handleMembershipSubmit = async (values: any) => {
    if (!selectedUser) return;

    try {
      const response = await userManagementAPI.addMembership(
        selectedUser.id,
        values.company_id,
        values.role
      );

      if (response.success) {
        message.success(response.message);
        setAddMembershipModalVisible(false);
        await loadUserMemberships(selectedUser.id);
        loadUsers(); // Refresh user list to update company count
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error('Üzvlük əlavə edilmədi');
    }
  };

  const handleRemoveMembership = async (membershipId: number) => {
    if (!selectedUser) return;

    try {
      const response = await userManagementAPI.removeMembership(selectedUser.id, membershipId);
      if (response.success) {
        message.success(response.message);
        await loadUserMemberships(selectedUser.id);
        loadUsers(); // Refresh user list to update company count
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error('Üzvlük silinmədi');
    }
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      'owner': 'gold',
      'admin': 'red',
      'manager': 'blue',
      'employee': 'green',
      'viewer': 'default'
    };
    return colors[role] || 'default';
  };

  const getRoleIcon = (role: string) => {
    const icons: Record<string, React.ReactNode> = {
      'owner': <CrownOutlined />,
      'admin': <SafetyOutlined />,
      'manager': <TeamOutlined />,
      'employee': <UserOutlined />,
      'viewer': <UserOutlined />
    };
    return icons[role] || <UserOutlined />;
  };

  const columns = [
    {
      title: 'İstifadəçi',
      key: 'user',
      render: (record: User) => (
        <div>
          <div className="font-medium">{record.full_name}</div>
          <div className="text-xs text-gray-500">{record.email}</div>
          <div className="text-xs text-gray-500">@{record.username}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: User) => (
        <Space direction="vertical" size="small">
          <Tag color={record.is_active ? 'green' : 'red'}>
            {record.is_active ? 'Aktiv' : 'Deaktiv'}
          </Tag>
          {record.is_superuser && <Tag color="gold" icon={<CrownOutlined />}>Superuser</Tag>}
          {record.is_staff && <Tag color="blue" icon={<SafetyOutlined />}>Staff</Tag>}
          {!record.is_crm_user && <Tag color="orange">CRM Deyil</Tag>}
        </Space>
      ),
    },
    {
      title: 'Şirkətlər',
      key: 'companies',
      render: (record: User) => (
        <div>
          <Badge count={record.company_count} showZero>
            <TeamOutlined style={{ fontSize: '16px' }} />
          </Badge>
          {record.current_company && (
            <div className="text-xs text-gray-500 mt-1">
              Hazırki: {record.current_company.name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Qoşulma Tarixi',
      dataIndex: 'date_joined',
      key: 'date_joined',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Son Giriş',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date: string | null) => 
        date ? dayjs(date).format('DD.MM.YYYY HH:mm') : 'Heç vaxt',
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (record: User) => (
        <Space size="small">
          <Tooltip title="Düzəliş et">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>
          
          <Tooltip title="Şirkət üzvlüklərini idarə et">
            <Button
              type="link"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => handleManageMemberships(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="İstifadəçini silmək istədiyinizdən əminsiniz?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Bəli"
            cancelText="Xeyr"
          >
            <Tooltip title="Sil">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="mb-0">
          <UserOutlined className="mr-2" />
          İstifadəçi İdarəetməsi
        </Title>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={handleCreateUser}
        >
          Yeni İstifadəçi
        </Button>
      </div>

      {/* Filterlər */}
      <Card className="mb-6">
        <Input
          placeholder="İstifadəçi adı, email və ya username ilə axtarış..."
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          allowClear
          style={{ width: 400 }}
        />
      </Card>

      {/* İstifadəçilər cədvəli */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} istifadəçi`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* İstifadəçi yaratma/düzəliş modalı */}
      <Modal
        title={selectedUser ? 'İstifadəçini Düzəliş Et' : 'Yeni İstifadəçi Yarat'}
        open={userModalVisible}
        onCancel={() => setUserModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserSubmit}
        >
          <Form.Item
            name="username"
            label="İstifadəçi adı"
            rules={[{ required: true, message: 'İstifadəçi adı tələb olunur' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email tələb olunur' },
              { type: 'email', message: 'Düzgün email daxil edin' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="first_name"
            label="Ad"
            rules={[{ required: true, message: 'Ad tələb olunur' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="last_name"
            label="Soyad"
            rules={[{ required: true, message: 'Soyad tələb olunur' }]}
          >
            <Input />
          </Form.Item>

          {!selectedUser && (
            <Form.Item
              name="password"
              label="Şifrə"
              rules={[{ required: true, message: 'Şifrə tələb olunur' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item name="is_active" label="Aktiv" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="is_staff" label="Staff" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="is_superuser" label="Superuser" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="is_crm_user" label="CRM İstifadəçisi" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => setUserModalVisible(false)}>
              Ləğv et
            </Button>
            <Button type="primary" htmlType="submit">
              {selectedUser ? 'Yenilə' : 'Yarat'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Şirkət üzvlükləri drawer */}
      <Drawer
        title={`${selectedUser?.full_name} - Şirkət Üzvlükləri`}
        placement="right"
        onClose={() => setMembershipDrawerVisible(false)}
        open={membershipDrawerVisible}
        width={600}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddMembership}
          >
            Üzvlük Əlavə Et
          </Button>
        }
      >
        <List
          dataSource={userMemberships}
          renderItem={(membership) => (
            <List.Item
              actions={[
                <Popconfirm
                  title="Bu üzvlüyü silmək istədiyinizdən əminsiniz?"
                  onConfirm={() => handleRemoveMembership(membership.id)}
                  okText="Bəli"
                  cancelText="Xeyr"
                >
                  <Button type="link" danger size="small">
                    Sil
                  </Button>
                </Popconfirm>
              ]}
            >
              <List.Item.Meta
                avatar={getRoleIcon(membership.role)}
                title={
                  <Space>
                    {membership.company.name}
                    <Tag color={getRoleColor(membership.role)}>
                      {membership.role_display}
                    </Tag>
                    {!membership.is_active && <Tag color="red">Deaktiv</Tag>}
                  </Space>
                }
                description={
                  <div>
                    <div>Qoşulma: {dayjs(membership.joined_at).format('DD.MM.YYYY')}</div>
                    <Space size="small" className="mt-1">
                      {membership.can_manage_students && <Tag color="blue">Tələbə İdarəsi</Tag>}
                      {membership.can_view_reports && <Tag color="green">Hesabat Görə Bilər</Tag>}
                      {membership.can_manage_users && <Tag color="orange">İstifadəçi İdarəsi</Tag>}
                    </Space>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>

      {/* Üzvlük əlavə etmə modalı */}
      <Modal
        title="Yeni Şirkət Üzvlüyü Əlavə Et"
        open={addMembershipModalVisible}
        onCancel={() => setAddMembershipModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={membershipForm}
          layout="vertical"
          onFinish={handleMembershipSubmit}
        >
          <Form.Item
            name="company_id"
            label="Şirkət"
            rules={[{ required: true, message: 'Şirkət seçin' }]}
          >
            <Select placeholder="Şirkət seçin" loading={availableCompanies.length === 0}>
              {availableCompanies.map(company => (
                <Option key={company.id} value={company.id}>
                  {company.name} ({company.city})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="role"
            label="Rol"
            rules={[{ required: true, message: 'Rol seçin' }]}
            initialValue="employee"
          >
            <Select>
              <Option value="owner">Sahibi</Option>
              <Option value="admin">Administrator</Option>
              <Option value="manager">Menecer</Option>
              <Option value="employee">İşçi</Option>
              <Option value="viewer">Baxıcı</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => setAddMembershipModalVisible(false)}>
              Ləğv et
            </Button>
            <Button type="primary" htmlType="submit">
              Əlavə Et
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;